import { AddLicensesDto } from '../generated-types/addLicensesDto';
import { CreatePortalSessionDto } from '../generated-types/createPortalSessionDto';
import { ExistingAddLicensesSessionDto } from '../generated-types/existingAddLicensesSessionDto';
import { PaymentState } from '../generated-types/paymentState';
import { PortalDto } from '../generated-types/portalDto';
import { AbstractApi } from './AbstractApi';

/**
 * The payments api interacts with the backend to create and manage payments,
 * which are driven by Stripe.
 */
export default class PaymentsApi extends AbstractApi {
  /**
   * Create a checkout session for licenses subscription
   * @returns The client secret for the session
   */
  async createLicenseCheckoutSession(): Promise<string> {
    const response = await this.authFetch(
      this.BuildUrl('payments/license-checkout-session'),
      {
        method: 'POST',
      }
    );

    if (!response.ok) throw Error();

    return response.text();
  }

  /**
   * Get the payment state for the current user's client onboarding
   * @returns The payment state
   */
  async getClientOnboardingPaymentState(): Promise<PaymentState> {
    const response = await this.authFetch(
      this.BuildUrl('payments/client-onboarding-state'),
      {
        method: 'Get',
      }
    );

    if (!response.ok) throw Error();
    return response.text() as unknown as PaymentState;
  }

  /**
   * Get the payment state for adding licenses
   * @returns The payment state
   */
  async getAddLicensesPaymentState(): Promise<PaymentState> {
    const response = await this.authFetch(
      this.BuildUrl('payments/add-licenses-state'),
      {
        method: 'Get',
      }
    );

    if (!response.ok) throw Error();
    return response.text() as unknown as PaymentState;
  }

  /**
   * Create a checkout session to add licenses to an existing subscription
   * @param addLicensesDto The add licenses dto
   * @returns The client secret for the session
   */
  async createAddLicensesCheckoutSession(
    addLicensesDto: AddLicensesDto
  ): Promise<string> {
    const response = await this.authFetch(
      this.BuildUrl('payments/add-licenses-checkout-session'),
      {
        method: 'POST',
        body: JSON.stringify(addLicensesDto),
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) throw Error();

    return response.text();
  }

  /**
   * Get the Stripe public key.
   * @returns The Stripe public key.
   */
  async getStripePublicKey(): Promise<string> {
    const response = await this.authFetch(
      this.BuildUrl('payments/stripe-public-key'),
      {
        method: 'GET',
      }
    );

    if (!response.ok) throw new Error('Failed to get Stripe public key');

    return response.text();
  }

  /**
   * Check if there an existing session already for the organization
   * @returns Details about the existing session, if one exists
   */
  async checkForExistingAddLicensesSession(): Promise<ExistingAddLicensesSessionDto> {
    const response = await this.authFetch(
      this.BuildUrl('payments/existing-add-licenses-session'),
      {
        method: 'GET',
      }
    );

    if (!response.ok) throw new Error('Failed to check for existing session.');

    return (await response.json()) as ExistingAddLicensesSessionDto;
  }

  /**
   * End an existing add licenses checkout session.
   * @returns A promise that resolves when the session is ended.
   */
  async EndAddLicensesSession(): Promise<void> {
    const response = await this.authFetch(
      this.BuildUrl('payments/end-add-licenses-session'),
      {
        method: 'DELETE',
      }
    );

    if (!response.ok) {
      if (response.status === 409) {
        throw new Error('A checkout session is already ending.');
      } else if (response.status === 400) {
        throw new Error('No active checkout session exists to ended.');
      } else {
        throw new Error('Failed to end the checkout session.');
      }
    }
  }

  /**
   * Create a customer portal
   * @param createPortalSession The details for the create portal session
   */
  async CreatePortalSession(
    createPortalSession: CreatePortalSessionDto
  ): Promise<PortalDto> {
    const response = await this.authFetch(this.BuildUrl('payments/portal'), {
      method: 'POST',
      body: JSON.stringify(createPortalSession),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) throw new Error('Failed to create customer portal.');

    return (await response.json()) as PortalDto;
  }
}
